<script setup lang="ts">
import { computed } from 'vue';
import { TableGridItem, Item } from '@/models/table-grid-interface';
import { useLocalization } from '@/composables';
import { shortenCutEnd } from '@/utils/utils';
import emptyAvatar from '@/assets/images/avatar_undefined.png';

const { lt } = useLocalization('uiRevolverMatrix');

const props = withDefaults(defineProps<{
  user: TableGridItem | null;
  titleField: string;
  subtitleField: string;
  titleFieldMax: number;
  subtitleFieldMax: number;
  cardBackgroundColor: string;
  // New props
  cardClass?: string;
  emptyCardClass?: string;
  showEmptyIcon?: boolean;
  emptyText?: string;
}>(), {
  cardClass: '',
  emptyCardClass: '',
  showEmptyIcon: true,
  emptyText: '',
});

const emit = defineEmits<{
  (e: 'click', user: TableGridItem | null): void;
}>();

const title = computed(() => {
  if (!props.user) return props.emptyText || lt('empty_slot');
  const value = props.user[props.titleField]?.presentable || props.user[props.titleField]?.raw || '';
  return shortenCutEnd(String(value), props.titleFieldMax);
});

const subtitle = computed(() => {
  if (!props.user) return '';
  const value = props.user[props.subtitleField]?.presentable || props.user[props.subtitleField]?.raw || '';
  return shortenCutEnd(String(value), props.subtitleFieldMax);
});

const avatarUrl = computed(() => {
  if (!props.user) {
    return props.showEmptyIcon ? emptyAvatar : '';
  }
  return Item(props.user).avatarUrl();
});

const cardClasses = computed(() => {
  const classes = ['rounded-lg', 'overflow-hidden'];
  if (props.user) {
    if (props.cardClass) classes.push(props.cardClass);
  } else {
    if (props.emptyCardClass) classes.push(props.emptyCardClass);
  }
  return classes;
});
</script>

<template>
  <v-tooltip location="top">
    <template #activator="{ props: tooltipProps }">
      <div
        v-bind="tooltipProps"
        :class="cardClasses"
        :style="{
          width: '100%',
          aspectRatio: '1',
          backgroundColor: cardBackgroundColor,
          cursor: 'pointer'
        }"
        @click="emit('click', user)"
      >
        <img
          v-if="avatarUrl"
          :src="avatarUrl"
          :alt="title"
          :style="{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }"
        >
        <div
          v-else
          class="d-flex align-center justify-center"
          :style="{ width: '100%', height: '100%' }"
        >
          <span class="text-caption text-grey">{{ title }}</span>
        </div>
      </div>
    </template>
    <div class="text-center">
      <div class="font-weight-bold">{{ title }}</div>
      <div v-if="subtitle" class="text-caption">{{ subtitle }}</div>
    </div>
  </v-tooltip>
</template>
